import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  PieChart,
  Pie,
  LabelList,
  Tooltip,
} from 'recharts';
import { Tooltip as MuiTooltip, useMediaQuery } from '@mui/material';
import { GenericDialog } from 'app/components/Alerts/GenericDialog';
import { makeStyles } from 'tss-react/mui';
import { useWindowResize } from 'utils/customHooks/useWindowResize';
import type { IMyPerformanceDashboardData, IMetricChartConfig } from './MyPerformanceDashboard.types';
import type { WidgetKey } from './MyPerformance.redux';
import {
  BarChartSkeleton,
  DonutSkeleton,
  ProgressBarsSkeleton,
  CriticalTasksSkeleton,
  MyCandidatesSkeleton,
  PipelineSkeleton,
  PipelineAlertsSkeleton,
} from './MyPerformanceDashboardSkeletons';

const useStyles = makeStyles()(() => ({
  mainContent: {
    padding: '0 16px 16px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    boxSizing: 'border-box' as const,
  },
  chartsContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    background: '#fff',
    overflow: 'hidden',
    width: '100%',
    '& > :last-child': { borderBottom: 'none', borderRight: 'none' },
  },
  chartCell: {
    minWidth: 0,
    minHeight: 300,
    borderRight: '1px solid #eee',
    borderBottom: '1px solid #eee',
    overflow: 'hidden',
    boxSizing: 'border-box' as const,
    '&:nth-child(2n)': { borderRight: 'none' },
    '&:last-child': { gridColumn: '1 / -1', minHeight: 0 },
    '@media (min-width: 1920px)': { minHeight: 380 },
  },
  metricBarChart: {
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
    minWidth: 0,
    minHeight: 300,
    padding: 18,
    overflow: 'hidden',
    boxSizing: 'border-box' as const,
    '@media (min-width: 1920px)': { minHeight: 380 },
  },
  widgetTitle: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 10,
    color: '#444',
    letterSpacing: 0.2,
  },
  chartBody: {
    display: 'flex',
    alignItems: 'flex-end',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  chartArea: {
    flex: 1,
    height: 200,
    '@media (min-width: 1920px)': { height: 280 },
  },
  metricPanel: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    marginLeft: 14,
    minWidth: 64,
    flexShrink: 0,
  },
  metricLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: '#999',
  },
  metricValue: {
    fontSize: 26,
    fontWeight: 700,
    lineHeight: 1.1,
    color: '#333',
  },
  metricDivider: {
    width: '100%',
    height: 1,
    background: '#ddd',
    margin: '4px 0',
  },
  changeIndicator: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 13,
    fontWeight: 600,
    marginTop: 4,
  },
  changeArrow: {
    fontSize: 10,
    marginRight: 2,
  },
  cancelationsWidget: {
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
    minWidth: 0,
    padding: 18,
    overflow: 'hidden',
    boxSizing: 'border-box' as const,
  },
  cancelationsBody: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  donutContainer: {
    width: 200,
    height: 200,
    position: 'relative' as const,
    flexShrink: 0,
    '@media (min-width: 1920px)': { width: 260, height: 260 },
  },
  donutChartLayer: {
    position: 'relative' as const,
    zIndex: 1,
  },
  donutCenter: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center' as const,
    zIndex: 0,
  },
  donutCenterValue: {
    fontSize: 26,
    fontWeight: 900,
    color: '#222',
    lineHeight: 1,
  },
  donutLabel: {
    fontSize: 10,
    fontWeight: 600,
    position: 'absolute' as const,
    color: '#777',
  },
  legendList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 12,
    marginLeft: 12,
    fontSize: 13,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  legendDot: {
    display: 'inline-block',
    borderRadius: '50%',
    width: 9,
    height: 9,
    flexShrink: 0,
  },
  legendText: {
    color: '#555',
    fontSize: 13,
  },
  rightSidePanel: {
    background: '#fff',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 20,
    justifyContent: 'center',
    padding: 18,
    minWidth: 0,
    minHeight: 300,
    width: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box' as const,
    '@media (min-width: 1920px)': { minHeight: 380 },
  },
  progressLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
    gap: 10,
  },
  progressLabelText: {
    fontSize: 14,
    fontWeight: 700,
    whiteSpace: 'nowrap' as const,
    color: '#333',
  },
  progressLabelValue: {
    fontSize: 14,
    fontWeight: 500,
    color: '#666',
  },
  progressTrack: {
    width: '100%',
    borderRadius: 999,
    height: 14,
    background: '#eeeeee',
  },
  progressFill: {
    borderRadius: 999,
    height: 14,
  },
  criticalTasks: {
    background: '#fff',
    padding: 16,
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box' as const,
  },
  criticalTitle: {
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 12,
    color: '#333',
    letterSpacing: 0.2,
  },
  criticalGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  criticalCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: 12,
    border: '1.5px solid #c62828',
    background: '#fff',
    minWidth: 0,
  },
  criticalCount: {
    fontSize: 24,
    fontWeight: 800,
    color: '#b71c1c',
    lineHeight: 1,
    flexShrink: 0,
  },
  criticalLabel: {
    fontSize: 9,
    fontWeight: 700,
    lineHeight: 1.25,
    letterSpacing: '0.03em',
    color: '#b71c1c',
    whiteSpace: 'pre-line' as const,
    flex: 1,
    minWidth: 0,
  },
  myCandidates: {
    background: '#fff',
    padding: 16,
    width: '100%',
    minWidth: 0,
    minHeight: 0,
    boxSizing: 'border-box' as const,
    overflow: 'hidden',
  },
  candidatesTitle: {
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 8,
    color: '#444',
    letterSpacing: 0.2,
  },
  candidatesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: 10,
    width: '100%',
    minWidth: 0,
  },
  candidateCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: 10,
    borderRadius: 4,
    border: '1px solid #e0e0e0',
    background: '#fff',
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  candidateCount: {
    fontSize: 20,
    fontWeight: 800,
    color: '#333',
    lineHeight: 1,
    flexShrink: 0,
  },
  candidateLabel: {
    fontSize: 8,
    fontWeight: 700,
    lineHeight: 1.25,
    textTransform: 'uppercase' as const,
    color: '#888',
    whiteSpace: 'pre-line' as const,
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
  },
  pipelineWidget: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: 16,
    background: '#fff',
    overflow: 'hidden',
  },
  pipelineTitle: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 16,
    color: '#444',
    letterSpacing: 0.2,
  },
  pipelineBody: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    flexWrap: 'wrap' as const,
  },
  pipelineDonutContainer: {
    width: 140,
    height: 140,
    position: 'relative' as const,
    flexShrink: 0,
  },
  pipelineDonutChartLayer: {
    position: 'relative' as const,
    zIndex: 1,
  },
  pipelineDonutCenter: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center' as const,
    zIndex: 0,
  },
  pipelineDonutValue: {
    fontSize: 26,
    fontWeight: 700,
    color: '#555',
    lineHeight: 1,
  },
  pipelineDonutLabel: {
    fontSize: 7,
    fontWeight: 500,
    marginTop: 4,
    letterSpacing: '0.1em',
    color: '#999',
  },
  stagesContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    flex: 1,
    minWidth: 0,
  },
  stageGroup: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  stageColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    flexShrink: 0,
  },
  stageName: {
    fontSize: 10,
    fontWeight: 500,
    textAlign: 'center' as const,
    lineHeight: 1.2,
    color: '#888',
    whiteSpace: 'pre-line' as const,
    marginBottom: 6,
    minHeight: 28,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  stageCircleOuter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    width: 54,
    height: 54,
    background: '#fff',
    padding: 4,
    zIndex: 2,
  },
  stageCircleInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    color: '#fff',
    fontSize: 14,
    fontWeight: 800,
    width: '100%',
    height: '100%',
  },
  stageConnector: {
    flex: 1,
    height: 4,
    marginLeft: -2,
    marginRight: -2,
    zIndex: 1,
    marginBottom: 25,
  },
  alertsColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 12,
    minWidth: 180,
    flexShrink: 0,
    flex: '0 0 auto',
  },
  alertRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  alertBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 12,
    fontWeight: 700,
    minWidth: 42,
    height: 26,
    padding: '0 10px',
    borderRadius: 5,
  },
  alertLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#333',
  },
  chartWidgetClickable: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.02)',
    },
  },
  chartModalPaper: {
    width: '95vw',
    maxWidth: 1600,
    height: '90vh',
    minHeight: 650,
  },
  chartModalPaperFullScreen: {
    width: '100vw',
    height: '100vh',
    maxWidth: 'none',
  },
  chartModalContent: {
    padding: '16px 24px',
    width: '100%',
    overflow: 'auto',
    backgroundColor: '#f5f5f5',
  },
  modalChartContainer: {
    width: '100%',
    minHeight: 'calc(90vh - 100px)',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box' as const,
    overflow: 'hidden',
  },
}));

function CustomBarLabel(props: {
  x?: number;
  y?: number;
  width?: number;
  topLabels: string[];
  index?: number;
  fontSize?: number;
}) {
  const { x = 0, y = 0, width = 0, index = 0, topLabels, fontSize = 9 } = props;
  return (
    <text x={x + width / 2} y={y - 4} fill="#666" fontSize={fontSize} fontWeight={600} textAnchor="middle">
      {topLabels[index] || ''}
    </text>
  );
}

const tooltipStyle = {
  padding: '8px 12px',
  background: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: 4,
  fontSize: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
} as const;

const CHART_WIDGETS: WidgetKey[] = ['TOA', 'GM', 'Retention'];

function MetricChartModalContent({
  cfg,
  classes,
  isFullScreen,
}: {
  cfg: IMetricChartConfig;
  classes: any;
  isFullScreen?: boolean;
}) {
  const chartHeight = isFullScreen ? 'calc(100vh - 120px)' : 480;
  return (
    <div className={classes.modalChartContainer}>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', flex: 1, minHeight: chartHeight, width: '100%' }}>
          <div style={{ flex: 1, height: chartHeight, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={cfg.data}
                barCategoryGap="30%"
                margin={{ top: 32, right: 24, bottom: 12, left: 24 }}
              >
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const dataPoint = payload[0].payload as { month: string };
                    const idx = cfg.data.findIndex((d) => d.month === dataPoint.month);
                    const value = idx >= 0 ? cfg.topLabels[idx] : '';
                    return (
                      <div style={tooltipStyle}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{dataPoint.month}</div>
                        <div>Value: {value}</div>
                      </div>
                    );
                  }}
                />
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eaeaea" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 16, fill: '#555' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis hide />
                <Bar dataKey="s1" stackId="a" fill={cfg.barColors[0]} barSize={56} radius={[0, 0, 0, 0]} />
                <Bar dataKey="s2" stackId="a" fill={cfg.barColors[1]} barSize={56} radius={[0, 0, 0, 0]} />
                <Bar dataKey="s3" stackId="a" fill={cfg.barColors[2]} barSize={56} radius={[2, 2, 0, 0]}>
                  <LabelList content={<CustomBarLabel topLabels={cfg.topLabels} fontSize={15} />} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className={classes.metricPanel} style={{ minWidth: 100, marginLeft: 40 }}>
            <div className={classes.metricLabel} style={{ fontSize: 14 }}>Target</div>
            <div className={classes.metricValue} style={{ fontSize: 26 }}>{cfg.target || '—'}</div>
            <div className={classes.metricDivider} />
            <div className={classes.metricLabel} style={{ fontSize: 14 }}>Current</div>
            <div className={classes.metricValue} style={{ fontSize: 26 }}>{cfg.current}</div>
            <div
              className={classes.changeIndicator}
              style={{ color: cfg.changeUp ? '#388e3c' : '#d32f2f', fontSize: 14 }}
            >
              <span className={classes.changeArrow}>{cfg.changeUp ? '▲' : '▼'}</span>
              {cfg.changePercent}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export interface MyPerformanceDashboardContentProps {
  data: IMyPerformanceDashboardData;
  widgetLoaded: Partial<Record<WidgetKey, boolean>>;
  error?: boolean;
}

const RESIZE_DEBOUNCE_MS = 100;

const CANCELLATIONS_DONUT_RADII = { inner: 60, outer: 82 } as const;
const CANCELLATIONS_DONUT_RADII_LARGE = { inner: 78, outer: 107 } as const;

export const MyPerformanceDashboardContent = ({ data, widgetLoaded, error }: MyPerformanceDashboardContentProps) => {
  const { classes } = useStyles();
  const isLargeScreen = useMediaQuery('(min-width: 1920px)');
  const donutRadii = isLargeScreen ? CANCELLATIONS_DONUT_RADII_LARGE : CANCELLATIONS_DONUT_RADII;
  const [resizeKey, setResizeKey] = React.useState(0);
  const [chartModalConfig, setChartModalConfig] = React.useState<IMetricChartConfig | null>(null);
  const [chartModalFullScreen, setChartModalFullScreen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleResize = React.useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      setResizeKey(k => k + 1);
    }, RESIZE_DEBOUNCE_MS);
  }, []);
  useWindowResize(scheduleResize, scheduleResize);
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(scheduleResize);
    ro.observe(el);
    return () => { ro.disconnect(); if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [scheduleResize]);

  const {
    chartConfigs,
    cancelationData,
    cancelationCenterValue,
    sidePanelMetrics,
    criticalTasksData,
    candidatesRow1,
    candidatesRow2,
    pipelineStages,
    pipelineDonutData,
    pipelineCenterValue,
    pipelineAlerts,
  } = data;

  const pipelineCenterParts = pipelineCenterValue.split(/\s+/);
  const pipelineCenterNum = pipelineCenterParts[0] ?? pipelineCenterValue;
  const pipelineCenterLabel = pipelineCenterParts.slice(1).join(' ') || 'CANDIDATES';

  if (error) {
    return (
      <div className={classes.mainContent} data-testid="my-performance-dashboard">
        <div style={{ padding: 24, textAlign: 'center', color: '#d32f2f' }}>
          Unable to load dashboard data. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <>
    <div ref={containerRef} className={classes.mainContent} data-testid="my-performance-dashboard">
      <div className={classes.chartsContainer}>
          {chartConfigs.map((cfg, idx) => {
            const widgetKey = CHART_WIDGETS[idx];
            const loaded = widgetLoaded[widgetKey];
            return (
              <div key={cfg.title} className={classes.chartCell}>
                {loaded ? (
              <div
                data-testid={`widget-${cfg.title.toLowerCase().replace(/\s/g, '-')}`}
                className={`${classes.metricBarChart} ${classes.chartWidgetClickable}`}
                role="button"
                tabIndex={0}
                onClick={() => setChartModalConfig(cfg)}
                onKeyDown={(e) => e.key === 'Enter' && setChartModalConfig(cfg)}
              >
                <div className={classes.widgetTitle}>{cfg.title}</div>
                <div className={classes.chartBody}>
                  <div className={classes.chartArea}>
                    <ResponsiveContainer key={resizeKey} width="100%" height="100%">
                      <BarChart
                        data={cfg.data}
                        barCategoryGap="30%"
                        margin={{ top: 16, right: 2, bottom: 0, left: 2 }}
                      >
                        <Tooltip
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const dataPoint = payload[0].payload as { month: string };
                            const idx = cfg.data.findIndex((d) => d.month === dataPoint.month);
                            const value = idx >= 0 ? cfg.topLabels[idx] : '';
                            return (
                              <div style={tooltipStyle}>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>{dataPoint.month}</div>
                                <div>Value: {value}</div>
                              </div>
                            );
                          }}
                        />
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eaeaea" />
                        <XAxis
                          dataKey="month"
                          tick={{ fontSize: 12, fill: '#666' }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis hide />
                        <Bar dataKey="s1" stackId="a" fill={cfg.barColors[0]} barSize={36} radius={[0, 0, 0, 0]} />
                        <Bar dataKey="s2" stackId="a" fill={cfg.barColors[1]} barSize={36} radius={[0, 0, 0, 0]} />
                        <Bar dataKey="s3" stackId="a" fill={cfg.barColors[2]} barSize={36} radius={[2, 2, 0, 0]}>
                          <LabelList content={<CustomBarLabel topLabels={cfg.topLabels} />} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className={classes.metricPanel}>
                    <div className={classes.metricLabel}>Target</div>
                    <div className={classes.metricValue}>{cfg.target || '—'}</div>
                    <div className={classes.metricDivider} />
                    <div className={classes.metricLabel}>Current</div>
                    <div className={classes.metricValue}>{cfg.current}</div>
                    <div
                      className={classes.changeIndicator}
                      style={{ color: cfg.changeUp ? '#388e3c' : '#d32f2f' }}
                    >
                      <span className={classes.changeArrow}>{cfg.changeUp ? '▲' : '▼'}</span>
                      {cfg.changePercent}
                    </div>
                  </div>
                </div>
              </div>
                ) : (
                  <BarChartSkeleton />
                )}
            </div>
          );
          })}

          <div className={classes.chartCell}>
            {widgetLoaded.Cancellations ? (
            <div data-testid="widget-cancelations" className={classes.cancelationsWidget}>
              <div className={classes.widgetTitle}>Cancelations</div>
              <div className={classes.cancelationsBody}>
                <div className={classes.donutContainer}>
                  <div className={classes.donutChartLayer} style={{ width: '100%', height: '100%' }}>
                  <ResponsiveContainer key={resizeKey} width="100%" height="100%">
                    <PieChart>
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const entry = payload[0].payload as { name: string; value: number };
                          return (
                            <div style={tooltipStyle}>
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>{entry.name}</div>
                              <div>{entry.value}% of placements</div>
                            </div>
                          );
                        }}
                      />
                      <Pie
                        data={cancelationData}
                        cx="50%"
                        cy="50%"
                        innerRadius={donutRadii.inner}
                        outerRadius={donutRadii.outer}
                        dataKey="value"
                        stroke="none"
                        startAngle={90}
                        endAngle={-270}
                      >
                        {cancelationData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  </div>
                  <div className={classes.donutCenter}>
                    <div className={classes.donutCenterValue}>{cancelationCenterValue}</div>
                  </div>
                  {cancelationData.length >= 1 && (
                    <div className={classes.donutLabel} style={{ top: 4, left: 4 }}>
                      {cancelationData[0].value}%
                    </div>
                  )}
                  {cancelationData.length >= 2 && (
                    <div className={classes.donutLabel} style={{ top: 4, right: 0 }}>
                      {cancelationData[2]?.value ?? cancelationData[1].value}%
                    </div>
                  )}
                  {cancelationData.length >= 3 && (
                    <div className={classes.donutLabel} style={{ bottom: 12, left: 0 }}>
                      {cancelationData[1].value}%
                    </div>
                  )}
                </div>
                <div className={classes.legendList}>
                  {cancelationData.map((item) => (
                    <div key={item.name} className={classes.legendItem}>
                      <span className={classes.legendDot} style={{ background: item.color }} />
                      <span className={classes.legendText}>{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            ) : (
              <DonutSkeleton />
            )}
          </div>

        <div className={classes.chartCell}>
          <div data-testid="widget-right-panel" className={classes.rightSidePanel}>
            {widgetLoaded.ProgressBars ? (
              sidePanelMetrics.map((m, i) => (
              <MuiTooltip key={i} title={`${m.label}: ${m.value}`} arrow>
                <div>
                  <div className={classes.progressLabel}>
                    <span className={classes.progressLabelText}>{m.label}</span>
                    <span className={classes.progressLabelValue}>{m.value}</span>
                  </div>
                  <div className={classes.progressTrack}>
                    <div
                      className={classes.progressFill}
                      style={{ width: `${m.pct}%`, background: m.color }}
                    />
                  </div>
                </div>
              </MuiTooltip>
            ))
            ) : (
              <ProgressBarsSkeleton />
            )}
          </div>
        </div>

        <div className={classes.chartCell}>
          <div data-testid="widget-critical-tasks" className={classes.criticalTasks}>
            {widgetLoaded.CriticalTasks ? (
            <>
            <div className={classes.criticalTitle}>Critical Tasks</div>
            <div className={classes.criticalGrid}>
              {criticalTasksData.map((task, i) => (
                <MuiTooltip key={i} title={`${task.count} ${task.label}`} arrow>
                  <div data-testid={`card-critical-task-${i}`} className={classes.criticalCard}>
                    <span className={classes.criticalCount}>{task.count}</span>
                    <span className={classes.criticalLabel}>{task.label}</span>
                  </div>
                </MuiTooltip>
              ))}
            </div>
            </>
            ) : (
              <CriticalTasksSkeleton />
            )}
          </div>
        </div>

        <div className={classes.chartCell}>
          <div data-testid="widget-my-candidates" className={classes.myCandidates}>
            {widgetLoaded.CandidateCounts ? (
            <>
            <div className={classes.candidatesTitle}>My Candidates</div>
            <div className={classes.candidatesGrid}>
              {[...candidatesRow1, ...candidatesRow2].map((item, i) => (
                <MuiTooltip key={i} title={`${item.count} ${item.label}`} arrow>
                  <div data-testid={`card-candidate-${i}`} className={classes.candidateCard}>
                    <span className={classes.candidateCount}>{item.count}</span>
                    <span className={classes.candidateLabel}>{item.label}</span>
                  </div>
                </MuiTooltip>
              ))}
            </div>
            </>
            ) : (
              <MyCandidatesSkeleton />
            )}
          </div>
        </div>
      </div>

      <div data-testid="widget-my-pipeline" className={classes.pipelineWidget}>
        {widgetLoaded.Pipeline ? (
        <>
        <div className={classes.pipelineTitle}>My Pipeline</div>
        <div className={classes.pipelineBody}>
          <div className={classes.pipelineDonutContainer}>
            <div className={classes.pipelineDonutChartLayer} style={{ width: '100%', height: '100%' }}>
            <ResponsiveContainer key={resizeKey} width="100%" height="100%">
              <PieChart>
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const entry = payload[0].payload as { name: string; value: number };
                    const stage = pipelineStages.find((s) => s.name === entry.name);
                    return (
                      <div style={tooltipStyle}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{entry.name}</div>
                        <div>{entry.value}% of candidates</div>
                        {stage && <div>{stage.value} candidates</div>}
                      </div>
                    );
                  }}
                />
                <Pie
                  data={[{ value: 1 }]}
                  cx="50%"
                  cy="50%"
                  innerRadius={42}
                  outerRadius={58}
                  dataKey="value"
                  stroke="none"
                  fill="#ebebeb"
                  isAnimationActive={false}
                />
                <Pie
                  data={pipelineDonutData}
                  cx="50%"
                  cy="50%"
                  innerRadius={42}
                  outerRadius={58}
                  dataKey="value"
                  stroke="none"
                  startAngle={90}
                  endAngle={-270}
                >
                  {pipelineDonutData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            </div>
            <div className={classes.pipelineDonutCenter}>
              <div className={classes.pipelineDonutValue}>{pipelineCenterNum}</div>
              <div className={classes.pipelineDonutLabel}>{pipelineCenterLabel}</div>
            </div>
          </div>

          <div className={classes.stagesContainer}>
            {pipelineStages.map((stage, i) => (
              <div
                key={i}
                className={classes.stageGroup}
                style={i < pipelineStages.length - 1 ? { flex: 1 } : { flex: '0 0 auto' }}
              >
                <div className={classes.stageColumn}>
                  <span className={classes.stageName}>{stage.name}</span>
                  <MuiTooltip title={`${stage.name}: ${stage.value} candidates`} arrow>
                    <div
                      className={classes.stageCircleOuter}
                      style={{ border: `3px solid ${stage.color}` }}
                    >
                      <div
                        className={classes.stageCircleInner}
                        style={{ background: stage.color }}
                      >
                        {stage.value}
                      </div>
                    </div>
                  </MuiTooltip>
                </div>
                {i < pipelineStages.length - 1 && (
                  <div
                    className={classes.stageConnector}
                    style={{ background: stage.color }}
                  />
                )}
              </div>
            ))}
          </div>

          <div className={classes.alertsColumn}>
            {widgetLoaded.PipelineAlerts ? (
              pipelineAlerts.map((alert, i) => (
                <MuiTooltip key={i} title={`${alert.count} ${alert.label}`} arrow>
                  <div data-testid={`alert-pipeline-${i}`} className={classes.alertRow}>
                    <span
                      className={classes.alertBadge}
                      style={{ background: alert.color }}
                    >
                      {alert.count}
                    </span>
                    <span className={classes.alertLabel}>{alert.label}</span>
                  </div>
                </MuiTooltip>
              ))
            ) : (
              <PipelineAlertsSkeleton />
            )}
          </div>
        </div>
        </>
        ) : (
          <PipelineSkeleton />
        )}
      </div>
    </div>

    {chartModalConfig && (
      <GenericDialog
        open
        variant="blue"
        onClose={() => {
          setChartModalConfig(null);
          setChartModalFullScreen(false);
        }}
        classes={{
          paper: chartModalFullScreen ? classes.chartModalPaperFullScreen : classes.chartModalPaper,
        }}
        dialogTitleProps={{
          text: chartModalConfig.title,
          closeButton: true,
          expandable: true,
          onExpandChange: setChartModalFullScreen,
        }}
        dialogContentProps={{
          classes: { root: classes.chartModalContent },
        }}
      >
        <MetricChartModalContent
          cfg={chartModalConfig}
          classes={classes}
          isFullScreen={chartModalFullScreen}
        />
      </GenericDialog>
    )}
    </>
  );
};
